import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { AuthLayout, Field, inputClass, primaryButtonClass } from "@/components/AuthLayout";
import { tenantIdOf } from "@/lib/auth";
import { slugify } from "@/lib/slug";
import { isDemoMode, supabase } from "@/lib/supabase";

export const Route = createFileRoute("/onboarding")({
  beforeLoad: async ({ context }) => {
    if (isDemoMode) throw redirect({ to: "/" });
    const session = await context.auth.getSession();
    if (!session) throw redirect({ to: "/login", search: { redirect: "/onboarding" } });
    if (tenantIdOf(session)) throw redirect({ to: "/" });
  },
  component: OnboardingPage,
});

const currencies = ["PLN", "EUR", "USD", "GBP", "CZK", "SEK", "CHF"] as const;
const countries = ["PL", "DE", "CZ", "SK", "AT", "NL", "FR", "IT", "ES", "SE", "GB", "US"] as const;

function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [companyCode, setCompanyCode] = useState("PL01");
  const [companyName, setCompanyName] = useState("");
  const [currency, setCurrency] = useState<string>("PLN");
  const [country, setCountry] = useState<string>("PL");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);
  const regionNames = new Intl.DisplayNames([i18n.language], { type: "region" });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!supabase) return;
    setPending(true);
    setError(null);
    try {
      const { error: err } = await supabase.rpc("create_tenant", {
        p_name: name,
        p_slug: effectiveSlug,
        p_company_code: companyCode,
        p_company_name: companyName,
        p_currency: currency,
        p_country: country,
      });
      if (err) {
        if (err.code === "23505") throw new Error(t("onboarding.slugTaken"));
        if (err.code === "23514" && /limit/i.test(err.message)) throw new Error(t("onboarding.limitReached"));
        throw err;
      }
      // New token carries the tenant_id claim issued by the access-token hook.
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      queryClient.clear();
      await router.invalidate();
      await navigate({ to: "/" });
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : t("auth.errorGeneric"));
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout title={t("onboarding.title")} subtitle={t("onboarding.subtitle")}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field id="org-name" label={t("onboarding.orgName")}>
          <input
            id="org-name"
            required
            autoFocus
            maxLength={120}
            placeholder={t("onboarding.orgNamePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field id="slug" label={t("onboarding.slug")} hint={t("onboarding.slugHint")}>
          <input
            id="slug"
            required
            pattern="[a-z0-9][a-z0-9-]{1,46}[a-z0-9]"
            aria-describedby="slug-hint"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            className={`${inputClass} font-mono text-[13px]`}
          />
        </Field>
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
          <Field id="company-code" label={t("onboarding.companyCode")}>
            <input
              id="company-code"
              required
              pattern="[A-Za-z0-9][A-Za-z0-9_\-]{0,15}"
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
              className={`${inputClass} font-mono text-[13px]`}
            />
          </Field>
          <Field id="company-name" label={t("onboarding.companyName")}>
            <input
              id="company-name"
              required
              maxLength={160}
              placeholder={t("onboarding.companyNamePlaceholder")}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field id="currency" label={t("onboarding.currency")}>
            <select id="currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputClass}>
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field id="country" label={t("onboarding.country")}>
            <select id="country" value={country} onChange={(e) => setCountry(e.target.value)} className={inputClass}>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {regionNames.of(c) ?? c}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-(--status-danger-bg) px-3 py-2 text-[13px] text-(--status-danger-fg)">
            {error}
          </p>
        )}

        <button type="submit" disabled={pending} className={primaryButtonClass}>
          {pending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          {t("onboarding.submit")}
        </button>
      </form>
    </AuthLayout>
  );
}
